import React, { useMemo } from 'react';
import { useAppSelector } from 'hooks';

import { LoadingState } from '~libs/reduxUtils';

import Accordion from '~components/Accordion';
import Icon, { faAsterisk } from '~components/icon';

import { selectStatisticsLoading } from '~appointments/selectors/appointments';
import { selectCompanySettings } from '~main/selectors/login';
import { selectDerivedDateRange } from '~weekly-planner/selectors';
import {
  selectAwardCount,
  selectRowsLoading as selectAwardCountLoading,
} from '~weekly-planner/selectors/appointments/awardAlerts';
import {
  selectCount as selectConflictsCount,
  selectLoading as selectConflictsloading,
} from '~weekly-planner/selectors/appointments/conflicts';
import {
  selectLoading as selectMissingLoading,
  selectCount as selectMissingQualificationsCount,
} from '~weekly-planner/selectors/appointments/missingQualifications';
import {
  selectCount as selectRejectedCount,
  selectLoading as selectRejectedLoading,
} from '~weekly-planner/selectors/appointments/rejected';
import {
  selectAdjustedUnallocatedCount,
  selectLoading as selectUnallocatedLoading,
} from '~weekly-planner/selectors/appointments/unallocated';
import { selectAllChangesCount } from '~weekly-planner/selectors/unsaved';

import AutoAllocations from './AutoAllocations';
import { AwardAlerts } from './award-alerts';
import { Conflicts } from './conflicts';
import { MissingQualifications } from './missing-qualifications';
import { Rejected } from './rejected';
import Statistics from './Statistics';
import { UnallocatedAppointments } from './unallocated-appointments';

interface ComponentProps {
  onRefresh?: () => void;
}

const SideBarComponent: React.FC<ComponentProps> = ({ onRefresh }) => {
  const { week_start } = useAppSelector(selectDerivedDateRange);
  const unallocatedCount = useAppSelector(selectAdjustedUnallocatedCount);
  const awardCount = useAppSelector(selectAwardCount);
  const rejectedCount = useAppSelector(selectRejectedCount);
  const missingQualificationsCount = useAppSelector(selectMissingQualificationsCount);
  const changesCount = useAppSelector(selectAllChangesCount);
  const conflictCount = useAppSelector(selectConflictsCount);
  const conflictsLoading = useAppSelector(selectConflictsloading);
  const awardAlertsLoading = useAppSelector(selectAwardCountLoading);
  const missingQualificationsLoading = useAppSelector(selectMissingLoading);
  const rejectedLoading = useAppSelector(selectRejectedLoading);
  const statisticsLoading = useAppSelector(selectStatisticsLoading);
  const unallocatedLoading = useAppSelector(selectUnallocatedLoading);
  const { SYSTEM_2022_BLOCK_REJECT } = useAppSelector(selectCompanySettings);

  // Helper function to build titles with counts
  const buildTitle = (title: string, count: number | undefined, isLoading: LoadingState | undefined = undefined) => {
    if (isLoading === 'idle' || isLoading === 'pending') {
      return (
        <>
          <span className="me-2 ellipsis" title={title}>
            {title}
          </span>
          <Icon icon={faAsterisk} size="xs" title="Expand to load" />
        </>
      );
    }
    if (count && count > 0) {
      return (
        <span className="me-2 ellipsis" title={`${title} (${count})`}>
          {title} ({count})
        </span>
      );
    }
    return title;
  };

  // Memoize accordion items to prevent unnecessary re-renders
  const items = useMemo(
    () => [
      {
        header: 'Copy Allocations',
        bodyComponent: AutoAllocations,
        bodyProps: {
          weekStart: week_start,
          onRefresh,
        },
        hide: false,
      },
      {
        header: buildTitle('Conflicts', conflictCount, conflictsLoading),
        bodyComponent: Conflicts,
        bodyProps: {
          weekStart: week_start,
        },
        isLoading: conflictsLoading === 'pending',
      },
      {
        header: buildTitle('Rejected Appointments', rejectedCount, rejectedLoading),
        bodyComponent: Rejected,
        bodyProps: {
          weekStart: week_start,
        },
        isLoading: rejectedLoading === 'pending',
        hide: !!SYSTEM_2022_BLOCK_REJECT,
      },
      {
        header: buildTitle('Award Alerts', awardCount, awardAlertsLoading),
        bodyComponent: AwardAlerts,
        bodyProps: {
          weekStart: week_start,
        },
        isLoading: awardAlertsLoading === 'pending',
      },
      {
        header: 'Statistics',
        bodyComponent: Statistics,
        bodyProps: {
          weekStart: week_start,
        },
        isLoading: statisticsLoading === 'pending',
      },
      {
        header: buildTitle('Missing Qualifications', missingQualificationsCount, missingQualificationsLoading),
        bodyComponent: MissingQualifications,
        bodyProps: {
          weekStart: week_start,
        },
        isLoading: missingQualificationsLoading === 'pending',
      },
      {
        header: buildTitle('Unallocated Appointments', unallocatedCount, unallocatedLoading),
        bodyComponent: UnallocatedAppointments,
        bodyProps: {},
        isLoading: unallocatedLoading === 'pending',
      },
    ],
    [
      week_start,
      changesCount,
      conflictCount,
      conflictsLoading,
      rejectedCount,
      rejectedLoading,
      awardCount,
      awardAlertsLoading,
      statisticsLoading,
      missingQualificationsCount,
      missingQualificationsLoading,
      unallocatedCount,
      unallocatedLoading,
      onRefresh,
      SYSTEM_2022_BLOCK_REJECT,
    ],
  );

  // Only hide items where hide === true (undefined is treated as false)
  const visibleItems = items.filter((item) => item.hide !== true);
  return <Accordion className="side-bar-container" items={visibleItems} />;
};

// Use React.memo to prevent unnecessary re-renders
const SideBar = React.memo(SideBarComponent);
SideBar.displayName = 'SideBar';

export default SideBar;
